import {
  type FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import type { ChangeEvent, MouseEvent } from "react";

import { api, ApiClientError } from "../api";
import type {
  AgendaLocation,
  AvailabilityRule,
  Id,
  Professional,
  ProfessionalServiceSetting,
} from "../types";
import { useToast } from "../context/ToastContext";

type Service = {
  id: Id;
  code: string;
  name: string;
  family: string | null;
  active: boolean;
};

const WEEKDAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
];

function localDateValue() {
  const date = new Date();

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function timeValue(value?: string | null) {
  return value ? value.slice(0, 5) : "";
}

function weekdayLabel(value: number) {
  return (
    WEEKDAYS.find((weekday) => weekday.value === value)
      ?.label || "Día"
  );
}

export default function AgendaAvailabilityConfig() {
  const { show } = useToast();

  const [professionals, setProfessionals] =
    useState<Professional[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [locations, setLocations] =
    useState<AgendaLocation[]>([]);
  const [settings, setSettings] =
    useState<ProfessionalServiceSetting[]>([]);
  const [rules, setRules] =
    useState<AvailabilityRule[]>([]);
  const [loading, setLoading] = useState(false);

  const [settingProfessionalId, setSettingProfessionalId] =
    useState("");
  const [settingServiceId, setSettingServiceId] =
    useState("");
  const [durationMinutes, setDurationMinutes] =
    useState("60");
  const [bufferBeforeMinutes, setBufferBeforeMinutes] =
    useState("0");
  const [bufferAfterMinutes, setBufferAfterMinutes] =
    useState("0");
  const [settingActive, setSettingActive] =
    useState(true);
  const [savingSetting, setSavingSetting] =
    useState(false);

  const [showRuleForm, setShowRuleForm] =
    useState(false);
  const [editingRule, setEditingRule] =
    useState<AvailabilityRule | null>(null);
  const [ruleProfessionalId, setRuleProfessionalId] =
    useState("");
  const [ruleLocationId, setRuleLocationId] =
    useState("");
  const [weekday, setWeekday] = useState("1");
  const [startsTime, setStartsTime] =
    useState("09:00");
  const [endsTime, setEndsTime] =
    useState("18:00");
  const [timezone, setTimezone] =
    useState("America/Argentina/Buenos_Aires");
  const [slotIntervalMinutes, setSlotIntervalMinutes] =
    useState("30");
  const [validFrom, setValidFrom] =
    useState(localDateValue());
  const [validTo, setValidTo] = useState("");
  const [ruleActive, setRuleActive] = useState(true);
  const [ruleServiceIds, setRuleServiceIds] =
    useState<string[]>([]);
  const [savingRule, setSavingRule] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    const rangeStart = new Date();
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setDate(rangeEnd.getDate() + 1);

    try {
      const [
        professionalRows,
        serviceRows,
        calendar,
        settingResponse,
        ruleResponse,
      ] = await Promise.all([
        api.getProfessionals(),
        api.getServices(),
        api.getAgendaCalendar({
          from: rangeStart.toISOString(),
          to: rangeEnd.toISOString(),
        }),
        api.getProfessionalServiceSettings(),
        api.getAvailabilityRules({
          entity_type: "PROFESSIONAL",
        }),
      ]);

      setProfessionals(
        professionalRows.filter(
          (professional) => professional.active
        )
      );
      setServices(
        serviceRows.filter((service) => service.active)
      );
      setLocations(
        calendar.locations.filter(
          (location) => location.active
        )
      );
      setSettings(settingResponse.settings);
      setRules(ruleResponse.rules);
    } catch (error) {
      if (error instanceof ApiClientError) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo cargar la configuración de agenda.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  function editSetting(
    setting: ProfessionalServiceSetting
  ) {
    setSettingProfessionalId(
      String(setting.professional_id)
    );
    setSettingServiceId(String(setting.service_id));
    setDurationMinutes(
      String(setting.duration_minutes)
    );
    setBufferBeforeMinutes(
      String(setting.buffer_before_minutes)
    );
    setBufferAfterMinutes(
      String(setting.buffer_after_minutes)
    );
    setSettingActive(setting.active);
  }

  async function saveSetting(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    if (!settingProfessionalId || !settingServiceId) {
      show(
        "Elegí profesional y tratamiento.",
        "error"
      );
      return;
    }

    setSavingSetting(true);

    try {
      await api.saveProfessionalServiceSetting(
        settingProfessionalId as Id,
        settingServiceId as Id,
        {
          duration_minutes: Number(durationMinutes),
          buffer_before_minutes:
            Number(bufferBeforeMinutes),
          buffer_after_minutes:
            Number(bufferAfterMinutes),
          active: settingActive,
        }
      );

      show(
        "Duración y márgenes guardados.",
        "success"
      );
      await load();
    } catch (error) {
      if (error instanceof ApiClientError) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo guardar la duración.",
          "error"
        );
      }
    } finally {
      setSavingSetting(false);
    }
  }

  function openNewRule() {
    setEditingRule(null);
    setRuleProfessionalId("");
    setRuleLocationId("");
    setWeekday("1");
    setStartsTime("09:00");
    setEndsTime("18:00");
    setTimezone("America/Argentina/Buenos_Aires");
    setSlotIntervalMinutes("30");
    setValidFrom(localDateValue());
    setValidTo("");
    setRuleActive(true);
    setRuleServiceIds([]);
    setShowRuleForm(true);
  }

  function openEditRule(rule: AvailabilityRule) {
    setEditingRule(rule);
    setRuleProfessionalId(String(rule.entity_id));
    setRuleLocationId(
      rule.location_id == null
        ? ""
        : String(rule.location_id)
    );
    setWeekday(String(rule.weekday));
    setStartsTime(timeValue(rule.starts_time));
    setEndsTime(timeValue(rule.ends_time));
    setTimezone(rule.timezone);
    setSlotIntervalMinutes(
      String(rule.slot_interval_minutes)
    );
    setValidFrom(rule.valid_from.slice(0, 10));
    setValidTo(
      rule.valid_to ? rule.valid_to.slice(0, 10) : ""
    );
    setRuleActive(rule.active);
    setRuleServiceIds(
      rule.services.map((service) => String(service.id))
    );
    setShowRuleForm(true);
  }

  function toggleRuleService(serviceId: Id) {
    const value = String(serviceId);

    setRuleServiceIds((current) =>
      current.includes(value)
        ? current.filter((id) => id !== value)
        : [...current, value]
    );
  }

  async function saveRule(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    if (
      !ruleProfessionalId ||
      !startsTime ||
      !endsTime ||
      !validFrom
    ) {
      show(
        "Completá profesional, día, horario y vigencia.",
        "error"
      );
      return;
    }

    setSavingRule(true);

    try {
      const mutablePayload = {
        starts_time: startsTime,
        ends_time: endsTime,
        timezone,
        slot_interval_minutes:
          Number(slotIntervalMinutes),
        valid_to: validTo || null,
        active: ruleActive,
        service_ids: ruleServiceIds as Id[],
      };

      if (editingRule) {
        await api.updateAvailabilityRule(
          editingRule.id,
          mutablePayload
        );
        show("Regla horaria actualizada.", "success");
      } else {
        await api.createAvailabilityRule({
          entity_type: "PROFESSIONAL",
          entity_id: ruleProfessionalId as Id,
          location_id:
            ruleLocationId
              ? (ruleLocationId as Id)
              : null,
          weekday: Number(weekday),
          valid_from: validFrom,
          ...mutablePayload,
        });
        show("Regla horaria creada.", "success");
      }

      setShowRuleForm(false);
      setEditingRule(null);
      await load();
    } catch (error) {
      if (error instanceof ApiClientError) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo guardar la regla horaria.",
          "error"
        );
      }
    } finally {
      setSavingRule(false);
    }
  }

  async function deactivateRule(
    rule: AvailabilityRule
  ) {
    if (
      !window.confirm(
        "¿Desactivar esta regla? El historial se conservará."
      )
    ) {
      return;
    }

    try {
      await api.deactivateAvailabilityRule(rule.id);
      show(
        "Regla desactivada; el historial fue conservado.",
        "success"
      );
      await load();
    } catch (error) {
      if (error instanceof ApiClientError) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo desactivar la regla.",
          "error"
        );
      }
    }
  }

  return (
    <div className="agenda-availability-config">
      <section className="agenda-config-section">
        <div className="kanban-header">
          <div>
            <h2>Duración por tratamiento</h2>
            <p className="meta">
              Define duración clínica y márgenes antes y
              después para cada profesional.
            </p>
          </div>
        </div>

        <form
          className="agenda-config-form"
          onSubmit={saveSetting}
        >
          <label>
            <span>Profesional</span>
            <select
              value={settingProfessionalId}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setSettingProfessionalId(event.target.value)
              }
              required
            >
              <option value="">Elegí profesional...</option>
              {professionals.map((professional) => (
                <option
                  key={professional.id}
                  value={professional.id}
                >
                  {professional.full_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Tratamiento</span>
            <select
              value={settingServiceId}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setSettingServiceId(event.target.value)
              }
              required
            >
              <option value="">Elegí tratamiento...</option>
              {services.map((service) => (
                <option
                  key={service.id}
                  value={service.id}
                >
                  {service.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Duración (minutos)</span>
            <input
              type="number"
              min="5"
              max="720"
              value={durationMinutes}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setDurationMinutes(event.target.value)
              }
              required
            />
          </label>

          <label>
            <span>Margen previo</span>
            <input
              type="number"
              min="0"
              max="240"
              value={bufferBeforeMinutes}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setBufferBeforeMinutes(event.target.value)
              }
              required
            />
          </label>

          <label>
            <span>Margen posterior</span>
            <input
              type="number"
              min="0"
              max="240"
              value={bufferAfterMinutes}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setBufferAfterMinutes(event.target.value)
              }
              required
            />
          </label>

          <label className="agenda-config-checkbox">
            <input
              type="checkbox"
              checked={settingActive}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setSettingActive((event.currentTarget as HTMLInputElement).checked)
              }
            />
            <span>Configuración activa</span>
          </label>

          <button
            className="btn-primary"
            type="submit"
            disabled={savingSetting}
          >
            {savingSetting
              ? "Guardando..."
              : "Guardar duración"}
          </button>
        </form>

        {loading && (
          <div className="loading-strip">
            Cargando configuración...
          </div>
        )}

        <div className="agenda-config-list">
          {settings.map((setting) => (
            <button
              type="button"
              className="agenda-config-row"
              key={
                String(setting.professional_id) +
                ":" +
                String(setting.service_id)
              }
              onClick={() => editSetting(setting)}
            >
              <span>
                <strong>{setting.professional_name}</strong>
                <small>{setting.service_name}</small>
              </span>
              <span>
                {setting.duration_minutes} min
                {" · "}
                margen {setting.buffer_before_minutes}/
                {setting.buffer_after_minutes}
              </span>
              <span>
                {setting.active ? "Activa" : "Inactiva"}
              </span>
            </button>
          ))}

          {!loading && settings.length === 0 && (
            <p className="meta">
              Todavía no hay duraciones configuradas.
            </p>
          )}
        </div>
      </section>

      <section className="agenda-config-section">
        <div className="kanban-header">
          <div>
            <h2>Horarios recurrentes</h2>
            <p className="meta">
              Organiza días, franjas, sedes y tratamientos
              habilitados para cada profesional.
            </p>
          </div>

          <button
            className="btn-primary"
            type="button"
            onClick={openNewRule}
          >
            + Nueva regla
          </button>
        </div>

        <div className="agenda-config-list">
          {rules.map((rule) => (
            <div
              className="agenda-config-row"
              key={rule.id}
            >
              <span>
                <strong>
                  {weekdayLabel(rule.weekday)}
                  {" · "}
                  {timeValue(rule.starts_time)}
                  {"–"}
                  {timeValue(rule.ends_time)}
                </strong>
                <small>
                  {rule.entity_name || "Profesional"}
                  {rule.location_name
                    ? " · " + rule.location_name
                    : " · Todas las sedes"}
                </small>
              </span>

              <span>
                Cada {rule.slot_interval_minutes} min
                {" · "}
                {rule.services.length > 0
                  ? rule.services
                      .map((service) => service.name)
                      .join(", ")
                  : "Todos los tratamientos"}
              </span>

              <span>
                {rule.active ? "Activa" : "Inactiva"}
              </span>

              <div className="agenda-config-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => openEditRule(rule)}
                >
                  Editar
                </button>

                {rule.active && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => deactivateRule(rule)}
                  >
                    Desactivar
                  </button>
                )}
              </div>
            </div>
          ))}

          {!loading && rules.length === 0 && (
            <p className="meta">
              Todavía no hay horarios recurrentes.
            </p>
          )}
        </div>
      </section>

      {showRuleForm && (
        <div
          className="modal-backdrop"
          onClick={() => {
            if (!savingRule) setShowRuleForm(false);
          }}
        >
          <div
            className="modal modal-wide"
            onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
          >
            <h3>
              {editingRule
                ? "Editar regla horaria"
                : "Nueva regla horaria"}
            </h3>

            <form
              className="agenda-rule-form"
              onSubmit={saveRule}
            >
              <label>
                <span>Profesional</span>
                <select
                  value={ruleProfessionalId}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setRuleProfessionalId(
                      event.target.value
                    )
                  }
                  disabled={Boolean(editingRule)}
                  required
                >
                  <option value="">
                    Elegí profesional...
                  </option>
                  {professionals.map((professional) => (
                    <option
                      key={professional.id}
                      value={professional.id}
                    >
                      {professional.full_name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Día</span>
                <select
                  value={weekday}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setWeekday(event.target.value)
                  }
                  disabled={Boolean(editingRule)}
                >
                  {WEEKDAYS.map((day) => (
                    <option
                      key={day.value}
                      value={day.value}
                    >
                      {day.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Sede</span>
                <select
                  value={ruleLocationId}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setRuleLocationId(event.target.value)
                  }
                  disabled={Boolean(editingRule)}
                >
                  <option value="">Todas las sedes</option>
                  {locations.map((location) => (
                    <option
                      key={location.id}
                      value={location.id}
                    >
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Desde</span>
                <input
                  type="time"
                  value={startsTime}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setStartsTime(event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>Hasta</span>
                <input
                  type="time"
                  value={endsTime}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setEndsTime(event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>Intervalo (minutos)</span>
                <input
                  type="number"
                  min="5"
                  max="720"
                  value={slotIntervalMinutes}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setSlotIntervalMinutes(
                      event.target.value
                    )
                  }
                  required
                />
              </label>

              <label>
                <span>Zona horaria</span>
                <input
                  value={timezone}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setTimezone(event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>Válida desde</span>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setValidFrom(event.target.value)
                  }
                  disabled={Boolean(editingRule)}
                  required
                />
              </label>

              <label>
                <span>Válida hasta</span>
                <input
                  type="date"
                  value={validTo}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setValidTo(event.target.value)
                  }
                />
              </label>

              <label className="agenda-config-checkbox">
                <input
                  type="checkbox"
                  checked={ruleActive}
                  onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                    setRuleActive((event.currentTarget as HTMLInputElement).checked)
                  }
                />
                <span>Regla activa</span>
              </label>

              <fieldset className="agenda-rule-services">
                <legend>Tratamientos habilitados</legend>
                <p className="meta">
                  Sin selección, la regla admite todos los
                  tratamientos configurados para el profesional.
                </p>

                <div>
                  {services.map((service) => (
                    <label key={service.id}>
                      <input
                        type="checkbox"
                        checked={ruleServiceIds.includes(
                          String(service.id)
                        )}
                        onChange={() =>
                          toggleRuleService(service.id)
                        }
                      />
                      <span>{service.name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={savingRule}
                  onClick={() => setShowRuleForm(false)}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingRule}
                >
                  {savingRule
                    ? "Guardando..."
                    : "Guardar regla"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
